"use client";

import Image from "next/image";
import { useMediaQuery } from "@/hooks/use-media-query";
import { sendEvent } from "@/lib/utils/analytics";
import { type TouchpointType } from "@/lib/quiz-attribution";

const basePath = process.env.NEXT_PUBLIC_BASE_PATH || "/quiz";

interface ProductHighlightProps {
  onCtaClick?: () => void;
  touchpointType?: TouchpointType;
  badge?: string;
  title?: string;
  description?: string;
}

const bgDesktopImage =
    'https://cdn.inito.com/landing_page/section/product_info/ng_hover.png';
  const bgMobileImage =
    'https://cdn.inito.com/landing_page/section/product_info/ng_hover_mobile.png';

const ProductHighlight: React.FC<ProductHighlightProps> = ({
  onCtaClick,
  touchpointType,
  badge = "NEWLY LAUNCHED",
  title = "Our New Fertility Monitor",
  description = "Get the most precise<sup>1</sup> fertility hormone values with the standalone InSight Wireless Reader<sup>TM</sup>. Works with iOS and Android.",
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  return (
    <div className="w-full max-w-[500px] px-5 py-12 md:py-10">
      <div
        className="relative flex h-[450px] flex-col items-start gap-[10px] self-stretch overflow-hidden rounded-[16px] p-5 text-white md:h-[460px]"
        style={{
          background: isMobile
            ? `linear-gradient(4deg, #512908 12.7%, rgba(81, 41, 8, 0.00) 34.08%), linear-gradient(166deg, #502B0C 10.95%, rgba(97, 54, 18, 0.00) 53.23%), url(${bgMobileImage}) lightgray -76.721px -53.364px / 143.198% 126.73% no-repeat`
            : `linear-gradient(4deg, #512908 12.7%, rgba(81, 41, 8, 0.00) 34.08%), linear-gradient(35deg, rgba(97, 54, 18, 0.70) 17.62%, rgba(97, 54, 18, 0.00) 46.53%), linear-gradient(152deg, rgba(97, 54, 18, 0.90) 10.27%, rgba(97, 54, 18, 0.00) 40.12%), url(${bgMobileImage}) center / cover no-repeat`,
        }}
      >
        <div
          className="text-[#474646] font-secondary flex w-fit items-center justify-center gap-[5px] rounded-[5px] border-[0.5px] border-white px-[7px] py-1 captitle-10 font-bold md:captitle-14"
          style={{
            background: "linear-gradient(90deg, #EFE0FF 0%, #FFE8C7 100%)",
          }}
        >
          {badge}
          <img
            src="https://www.inito.com/quiz/sparkle.png"
            alt=""
            aria-hidden="true"
            className="h-3 w-3"
          />
        </div>

        <div className="flex-1 title-32 text-white">
          {title}
        </div>

        <div className="flex items-end justify-between w-full gap-[16px]">
          <div className="max-w-[280px] md:max-w-[340px]">
            <div
              className="font-secondary title-14 md:title-16"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </div>
          <button
            className="flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-full bg-white transition-transform hover:scale-105 md:h-[58px] md:w-[58px]"
            data-testid="product-highlight-cta"
            onClick={() => {
              onCtaClick?.();
              sendEvent("product_card_clicked", { quiz_source: "bottom_sheet", flow_type: "NG", touchpoint_type: touchpointType });
            }}
            aria-label="Track your hormones with Inito"
          >
            <img
              src={"https://www.inito.com/quiz/arrow-right.svg"}
              alt=""
              className="w-[24px] invert"
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductHighlight;
